import Papa from 'papaparse';
import {
  CsvColumnMapping,
  CsvEntityFieldConfig,
  CsvImporterConfig,
  CsvParsedRow,
  CsvRawRow,
  CsvRelationOption,
  CsvRowError,
  CsvValidationContext,
  CsvFieldTransformConfig,
  CsvManualOverrideMap,
  CsvEncoding,
  CsvEncodingInfo,
  CsvImportMetaState,
} from './csv-import.types';

// Hilfsfunktion: versucht, das Encoding anhand einfacher Heuristiken zu bestimmen
function detectEncodingFromBytes(view: Uint8Array): CsvEncodingInfo {
  // Sehr einfache Heuristik: prüfen auf gültige UTF-8-Sequenzen, sonst ISO/Windows-Variante annehmen
  let i = 0;
  const len = Math.min(view.length, 4096); // nur ersten 4KB betrachten
  let isUtf8 = true;

  while (i < len) {
    const byte1 = view[i++];
    if (byte1 < 0x80) {
      // ASCII
      continue;
    }
    // 2-Byte-Sequenz
    if (byte1 >= 0xc2 && byte1 <= 0xdf) {
      const byte2 = view[i++];
      if ((byte2 & 0xc0) !== 0x80) {
        isUtf8 = false;
        break;
      }
      continue;
    }
    // 3-Byte-Sequenz
    if (byte1 >= 0xe0 && byte1 <= 0xef) {
      const byte2 = view[i++];
      const byte3 = view[i++];
      if ((byte2 & 0xc0) !== 0x80 || (byte3 & 0xc0) !== 0x80) {
        isUtf8 = false;
        break;
      }
      continue;
    }
    // 4-Byte-Sequenz
    if (byte1 >= 0xf0 && byte1 <= 0xf4) {
      const byte2 = view[i++];
      const byte3 = view[i++];
      const byte4 = view[i++];
      if (
        (byte2 & 0xc0) !== 0x80 ||
        (byte3 & 0xc0) !== 0x80 ||
        (byte4 & 0xc0) !== 0x80
      ) {
        isUtf8 = false;
        break;
      }
      continue;
    }

    // sonst: illegal als UTF-8
    isUtf8 = false;
    break;
  }

  if (isUtf8) {
    return {
      detected: 'utf-8',
      selected: 'utf-8',
      confidence: 0.9,
    };
  }

  // Wenn wir hier sind, gehen wir pragmatisch von "latin1"/Windows-1252 aus
  return {
    detected: 'windows-1252',
    selected: 'windows-1252',
    confidence: 0.6,
  };
}

function decodeBytes(view: Uint8Array, encoding: CsvEncoding): { text: string; warning: string | null } {
  const normalized = encoding === 'latin1' ? 'iso-8859-1' : encoding;

  try {
    // TextDecoder ist in modernen Browsern verfügbar; für alte Encodings wie windows-1252
    // funktioniert es in vielen Engines, fällt aber ggf. auf replacement chars zurück.
    const decoder = new TextDecoder(normalized === 'unknown' ? 'utf-8' : (normalized as string));
    const text = decoder.decode(view);
    return { text, warning: null };
  } catch (e) {
    // Fallback: naive Latin1-Dekodierung
    let result = '';
    for (let i = 0; i < view.length; i++) {
      result += String.fromCharCode(view[i]);
    }
    return {
      text: result,
      warning:
        'Das Encoding der Datei konnte nicht eindeutig bestimmt werden. Werte wurden als Latin-1 dekodiert – bitte prüfe die Umlaute in der Vorschau.',
    };
  }
}

export async function parseCsvTextFromString(
  text: string,
): Promise<{ headers: string[]; rows: CsvRawRow[] }> {
  return new Promise((resolve, reject) => {
    Papa.parse<string[]>(text, {
      complete: (results) => {
        const data = results.data as string[][];
        if (!data.length) {
          resolve({ headers: [], rows: [] });
          return;
        }
        const [headers, ...rows] = data;
        resolve({ headers, rows });
      },
      error: (err: Error) => reject(err),
      skipEmptyLines: true,
    });
  });
}

export async function parseCsvFile(
  file: File,
): Promise<{ headers: string[]; rows: CsvRawRow[]; meta: CsvImportMetaState }> {
  const warnings: string[] = [];

  // 1) Datei als ArrayBuffer einlesen
  const arrayBuffer = await file.arrayBuffer();
  const view = new Uint8Array(arrayBuffer);

  // 2) Encoding heuristisch bestimmen
  const encodingInfo = detectEncodingFromBytes(view);

  // 3) Bytes mit dem (zunächst) erkannten Encoding dekodieren
  const { text, warning } = decodeBytes(view, encodingInfo.selected);
  if (warning) {
    warnings.push(warning);
  }

  // 4) CSV aus dem dekodierten Text parsen
  const { headers, rows } = await parseCsvTextFromString(text);

  return {
    headers,
    rows,
    meta: {
      encoding: encodingInfo,
      warnings,
    },
  };
}

// NEU: Datei mit explizit vorgegebenem Encoding neu einlesen und parsen
export async function reparseCsvFileWithEncoding(
  file: File,
  encoding: CsvEncoding,
): Promise<{ headers: string[]; rows: CsvRawRow[]; meta: CsvImportMetaState }> {
  const warnings: string[] = [];

  const arrayBuffer = await file.arrayBuffer();
  const view = new Uint8Array(arrayBuffer);

  const encodingInfo: CsvEncodingInfo = {
    detected: encoding,
    selected: encoding,
    confidence: null,
  };

  const { text, warning } = decodeBytes(view, encoding);
  if (warning) warnings.push(warning);

  const { headers, rows } = await parseCsvTextFromString(text);

  return {
    headers,
    rows,
    meta: {
      encoding: encodingInfo,
      warnings,
    },
  };
}

export function parseDateFlexible(raw: string): Date | null {
  const value = raw.trim();
  if (!value) return null;

  // DD.MM.YYYY
  let match = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (match) {
    const [, d, m, y] = match;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return isNaN(date.getTime()) ? null : date;
  }

  // DD.MM.YY
  match = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2})$/);
  if (match) {
    const [, d, m, yy] = match;
    const year = Number(yy) + 2000; // einfache Heuristik
    const date = new Date(year, Number(m) - 1, Number(d));
    return isNaN(date.getTime()) ? null : date;
  }

  // YYYY-MM-DD
  match = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (match) {
    const [, y, m, d] = match;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return isNaN(date.getTime()) ? null : date;
  }

  // DD/MM/YYYY
  match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (match) {
    const [, d, m, y] = match;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return isNaN(date.getTime()) ? null : date;
  }

  const timestamp = Date.parse(value);
  if (!isNaN(timestamp)) {
    return new Date(timestamp);
  }

  return null;
}

export function toBackendDateIso(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}T00:00:00.000Z`;
}

// Hilfsfunktion: Feld-Transforms anwenden (z.B. Split erster/zweiter Teil)
function applyFieldTransforms(
  raw: string,
  fieldConfig: CsvEntityFieldConfig<any, any>,
  rowIndex: number,
  errors: CsvRowError[],
): string {
  // Wenn keine Transforms konfiguriert sind: Rohwert nur trimmen
  if (!fieldConfig.transforms || fieldConfig.transforms.length === 0) {
    return raw.trim();
  }

  let current = raw;

  for (const step of fieldConfig.transforms as CsvFieldTransformConfig[]) {
    try {
      switch (step.type) {
        case 'splitPart': {
          const delimiter = step.config?.delimiter ?? ' ';
          const index = step.config?.index ?? 0;
          const trimPart = step.config?.trimPart ?? true;

          const parts = current.split(delimiter);

          if (index < 0 || index >= parts.length) {
            // Index auerhalb des Bereichs: als Transform-Fehler loggen, Ergebnis leer lassen
            errors.push({
              rowIndex,
              fieldKey: String(fieldConfig.key),
              type: 'TRANSFORM',
              message: `Konnte Teil ${index + 1} mit Trennzeichen "${delimiter}" nicht extrahieren`,
              originalValue: raw,
            });
            current = '';
            break;
          }

          let value = parts[index];
          if (trimPart) {
            value = value.trim();
          }
          current = value;
          break;
        }
        default: {
          // Unbekannter Transform-Typ: als UNKNOWN-Fehler loggen, Wert unverndert lassen
          errors.push({
            rowIndex,
            fieldKey: String(fieldConfig.key),
            type: 'UNKNOWN',
            message: `Unbekannter Transform-Typ "${(step as any).type}"`,
            originalValue: raw,
          });
          break;
        }
      }
    } catch (e: any) {
      errors.push({
        rowIndex,
        fieldKey: String(fieldConfig.key),
        type: 'TRANSFORM',
        message: e?.message || 'Fehler bei der Feld-Transformation',
        originalValue: raw,
      });
      // Bei Transform-Fehlern: Verarbeitung abbrechen, aktueller Wert bleibt
      break;
    }
  }

  return current.trim();
}

// Hilfsfunktionen
export function levenshteinDistance(a: string, b: string): number {
  const s = a.toLowerCase();
  const t = b.toLowerCase();
  const m = s.length;
  const n = t.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost,
      );
    }
  }

  return dp[m][n];
}

export function similarityScore(a: string, b: string): number {
  if (!a && !b) return 1;
  if (!a || !b) return 0;
  const distance = levenshteinDistance(a, b);
  const maxLen = Math.max(a.length, b.length);
  return maxLen === 0 ? 1 : 1 - distance / maxLen;
}

export function findBestFuzzyMatch(input: string, options: CsvRelationOption[], threshold = 0.8): CsvRelationOption | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  let best: CsvRelationOption | null = null;
  let bestScore = 0;

  for (const option of options) {
    const score = similarityScore(trimmed.toLowerCase(), option.name.toLowerCase());
    if (score > bestScore) {
      bestScore = score;
      best = option;
    }
  }

  if (best && bestScore >= threshold) return best;
  return null;
}

// Ab hier: alte Automapping-/Alias-/Synonym-Utilities wurden entfernt, da kein Automapping mehr verwendet wird.

export function autoMapCsvColumns<TDto = any, TMeta = any>(
  headers: string[],
  config: CsvImporterConfig<TDto, TMeta>,
  options?: { threshold?: number; deltaThreshold?: number; weights?: Partial<never> },
): CsvColumnMapping<TDto> {
  const mapping: CsvColumnMapping<TDto> = {};

  // Kein Automapping mehr: alle vorhandenen Header-Indices explizit auf null setzen
  headers.forEach((_, idx) => {
    mapping[idx] = null;
  });

  // config und options werden aktuell bewusst nicht verwendet, bleiben aber
  // aus API-Kompatibilitätsgründen erhalten.

  return mapping;
}

export async function validateCsvRows<TDto = any, TMeta = any>(
  rows: CsvRawRow[],
  mapping: CsvColumnMapping<TDto>,
  config: CsvImporterConfig<TDto, TMeta>,
  context: CsvValidationContext<TMeta>,
  manualOverrides?: CsvManualOverrideMap,
): Promise<CsvParsedRow<TDto>[]> {
  const parsed: CsvParsedRow<TDto>[] = [];

  rows.forEach((rawRow, rowIndex) => {
    const mappedValues: any = {};
    const errors: CsvRowError[] = [];

    const overridesForRow = manualOverrides?.[rowIndex] || {};

    Object.entries(mapping).forEach(([indexStr, fieldConfig]) => {
      const colIndex = Number(indexStr);
      if (!fieldConfig) return;

      const fieldKey = String(fieldConfig.key);
      const overrideValue = overridesForRow[fieldKey];

      // Wenn ein manueller Override existiert, diesen validieren und direkt als Wert nehmen
      if (overrideValue !== undefined) {
        let value: any = overrideValue;
        try {
          switch (fieldConfig.type) {
            case 'number': {
              const n = typeof overrideValue === 'number' ? overrideValue : Number(String(overrideValue).replace(',', '.'));
              if (Number.isNaN(n)) {
                errors.push({
                  rowIndex,
                  fieldKey,
                  type: 'TYPE',
                  message: 'Zahl erwartet',
                  originalValue: String(overrideValue),
                });
                return;
              }
              value = n;
              break;
            }
            case 'boolean': {
              if (typeof overrideValue === 'boolean') {
                value = overrideValue;
              } else {
                const lower = String(overrideValue).toLowerCase();
                if (['true', 'wahr', 'ja', '1'].includes(lower)) value = true;
                else if (['false', 'falsch', 'nein', '0'].includes(lower)) value = false;
                else {
                  errors.push({
                    rowIndex,
                    fieldKey,
                    type: 'TYPE',
                    message: 'Boolescher Wert erwartet (ja/nein, wahr/falsch, 1/0)',
                    originalValue: String(overrideValue),
                  });
                  return;
                }
              }
              break;
            }
            case 'date': {
              if (!overrideValue) {
                if (fieldConfig.required) {
                  errors.push({
                    rowIndex,
                    fieldKey,
                    type: 'TYPE',
                    message: 'Pflichtfeld darf nicht leer sein',
                    originalValue: String(overrideValue ?? ''),
                  });
                }
                return;
              }
              const d = new Date(overrideValue);
              if (Number.isNaN(d.getTime())) {
                errors.push({
                  rowIndex,
                  fieldKey,
                  type: 'DATE',
                  message: 'Ungültiges Datum',
                  originalValue: String(overrideValue),
                });
                return;
              }
              value = toBackendDateIso(d);
              break;
            }
            case 'relation': {
              // Override für Relationsfelder (z.B. Mannschaft) ist bereits eine ID.
              const id = overrideValue == null ? '' : String(overrideValue).trim();
              if (!id) {
                if (fieldConfig.required) {
                  errors.push({
                    rowIndex,
                    fieldKey,
                    type: 'TYPE',
                    message: 'Pflichtfeld darf nicht leer sein',
                    originalValue: String(overrideValue ?? ''),
                  });
                }
                return;
              }
              value = id;
              break;
            }
            case 'enum': {
              const enumRaw = String(overrideValue).trim();
              if (!enumRaw) {
                if (fieldConfig.required) {
                  errors.push({
                    rowIndex,
                    fieldKey,
                    type: 'TYPE',
                    message: 'Pflichtfeld darf nicht leer sein',
                    originalValue: String(overrideValue ?? ''),
                  });
                }
                return;
              }

              const allowed = fieldConfig.enumValues || [];
              if (allowed.length > 0) {
                const normalized = enumRaw.toLowerCase();
                const matched = allowed.find((v) => v.toLowerCase() === normalized);
                if (!matched) {
                  errors.push({
                    rowIndex,
                    fieldKey,
                    type: 'TYPE',
                    message: `Ungültiger Wert. Erlaubt: ${allowed.join(', ')}`,
                    originalValue: String(overrideValue),
                  });
                  return;
                }
                value = fieldConfig.enumNormalize ? fieldConfig.enumNormalize(matched) : matched;
              } else {
                value = enumRaw;
              }
              break;
            }
            default: {
              // string: als String übernehmen
              const str = String(overrideValue).trim();
              if (!str && fieldConfig.required) {
                errors.push({
                  rowIndex,
                  fieldKey,
                  type: 'TYPE',
                  message: 'Pflichtfeld darf nicht leer sein',
                  originalValue: String(overrideValue),
                });
                return;
              }
              value = str || undefined;
            }
          }

          if (value !== undefined) {
            mappedValues[fieldKey] = value;
          }
        } catch (e: any) {
          errors.push({
            rowIndex,
            fieldKey,
            type: 'UNKNOWN',
            message: e?.message || 'Unbekannter Fehler bei der Verarbeitung',
            originalValue: String(overrideValue),
          });
        }
        return;
      }

      // Kein Override: bisherige CSV-Logik nutzen
      const raw = rawRow[colIndex] ?? '';
      const trimmed = raw.trim();

      // Leere Zelle: wenn Fallback vorhanden, diesen Wert nutzen; sonst Feld nur dann ignorieren,
      // wenn das Feld nicht als required markiert ist.
      if (!trimmed) {
        if (fieldConfig.fallbackValue !== undefined) {
          mappedValues[fieldConfig.key as string] = fieldConfig.fallbackValue;
          return;
        }

        if (fieldConfig.required) {
          errors.push({
            rowIndex,
            fieldKey: String(fieldConfig.key),
            type: 'TYPE',
            message: 'Pflichtfeld darf nicht leer sein',
            originalValue: raw,
          });
        }
        return;
      }

      // NEU: zunächst Transformationskette auf den Rohwert anwenden
      const transformed = applyFieldTransforms(raw, fieldConfig, rowIndex, errors);
      const effective = transformed.trim();

      // Wenn nach der Transformation nichts übrig bleibt, Feld nur dann ignorieren,
      // wenn es nicht als required markiert ist oder ein Fallback vorhanden ist.
      if (!effective) {
        if (fieldConfig.fallbackValue !== undefined) {
          mappedValues[fieldConfig.key as string] = fieldConfig.fallbackValue;
          return;
        }

        if (fieldConfig.required) {
          errors.push({
            rowIndex,
            fieldKey: String(fieldConfig.key),
            type: 'TYPE',
            message: 'Pflichtfeld darf nicht leer sein',
            originalValue: raw,
          });
        }
        return;
      }

      let value: any = effective;

      try {
        switch (fieldConfig.type) {
          case 'number': {
            const n = Number(effective.replace(',', '.'));
            if (Number.isNaN(n)) {
              errors.push({
                rowIndex,
                fieldKey: String(fieldConfig.key),
                type: 'TYPE',
                message: 'Zahl erwartet',
                originalValue: raw,
              });
              return;
            }
            value = n;
            break;
          }
          case 'boolean': {
            const lower = effective.toLowerCase();
            if (['true', 'wahr', 'ja', '1'].includes(lower)) value = true;
            else if (['false', 'falsch', 'nein', '0'].includes(lower)) value = false;
            else {
              errors.push({
                rowIndex,
                fieldKey: String(fieldConfig.key),
                type: 'TYPE',
                message: 'Boolescher Wert erwartet (ja/nein, wahr/falsch, 1/0)',
                originalValue: raw,
              });
              return;
            }
            break;
          }
          case 'date': {
            const date = parseDateFlexible(effective);
            if (!date) {
              errors.push({
                rowIndex,
                fieldKey: String(fieldConfig.key),
                type: 'DATE',
                message: 'Ungültiges Datum. Erwartete Formate: TT.MM.JJJJ, TT.MM.JJ, JJJJ-MM-TT, TT/MM/JJJJ',
                originalValue: raw,
              });
              return;
            }
            value = toBackendDateIso(date);
            break;
          }
          case 'relation': {
            if (fieldConfig.relationResolver) {
              const resolved = fieldConfig.relationResolver(effective, context);
              if (resolved && 'then' in (resolved as any)) {
                errors.push({
                  rowIndex,
                  fieldKey: String(fieldConfig.key),
                  type: 'UNKNOWN',
                  message: 'Async-Relation-Resolver wird nicht unterstützt',
                  originalValue: raw,
                });
                return;
              }
              const option = resolved as CsvRelationOption | null;
              if (!option) {
                errors.push({
                  rowIndex,
                  fieldKey: String(fieldConfig.key),
                  type: 'RELATION',
                  message: `Konnte keinen passenden Eintrag für "${effective}" finden`,
                  originalValue: raw,
                });
                return;
              }
              value = option.id;
            } else if (context.relationOptions && fieldConfig.relationKey) {
              const options = context.relationOptions[fieldConfig.relationKey] || [];
              const best = findBestFuzzyMatch(effective, options);
              if (!best) {
                errors.push({
                  rowIndex,
                  fieldKey: String(fieldConfig.key),
                  type: 'RELATION',
                  message: `Konnte keinen passenden Eintrag für "${effective}" finden`,
                  originalValue: raw,
                });
                return;
              }
              value = best.id;
            } else {
              // fallback: roh übergeben
              value = effective;
            }
            break;
          }
          case 'enum': {
            const allowed = fieldConfig.enumValues || [];
            if (allowed.length > 0) {
              const normalized = effective.toLowerCase();
              const matched = allowed.find((v) => v.toLowerCase() === normalized);
              if (!matched) {
                errors.push({
                  rowIndex,
                  fieldKey: String(fieldConfig.key),
                  type: 'TYPE',
                  message: `Ungültiger Wert. Erlaubt: ${allowed.join(', ')}`,
                  originalValue: raw,
                });
                return;
              }
              value = fieldConfig.enumNormalize ? fieldConfig.enumNormalize(matched) : matched;
            } else {
              value = effective;
            }
            break;
          }
          case 'string':
          default: {
            value = effective || undefined;
          }
        }

        if (fieldConfig.parse) {
          value = fieldConfig.parse(effective, rawRow, context);
        }

        if (value !== undefined) {
          mappedValues[fieldConfig.key as string] = value;
        }
      } catch (e: any) {
        errors.push({
          rowIndex,
          fieldKey: String(fieldConfig.key),
          type: 'UNKNOWN',
          message: e?.message || 'Unbekannter Fehler bei der Verarbeitung',
          originalValue: raw,
        });
      }
    });

    // NEU: Manuelle Overrides für Felder validieren, die keiner CSV-Spalte zugeordnet sind
    // (z.B. Position nur per Dropdown gesetzt, ohne Spalten-Mapping)
    const mappedFieldKeys = new Set(
      Object.values(mapping)
        .filter(Boolean)
        .map((f) => String(f!.key)),
    );

    const unmappedOverridesForRow = manualOverrides?.[rowIndex] || {};
    Object.entries(unmappedOverridesForRow).forEach(([fieldKey, overrideValue]) => {
      // Nur Felder verarbeiten, die nicht bereits über das Mapping validiert wurden
      if (mappedFieldKeys.has(fieldKey)) return;
      if (overrideValue === undefined) return;

      const fieldConfig = config.fields.find((f) => String(f.key) === fieldKey);
      if (!fieldConfig) return;

      let value: any = overrideValue;

      try {
        switch (fieldConfig.type) {
          case 'enum': {
            const enumRaw = String(overrideValue).trim();
            if (!enumRaw) {
              if (fieldConfig.required) {
                errors.push({
                  rowIndex,
                  fieldKey,
                  type: 'TYPE',
                  message: 'Pflichtfeld darf nicht leer sein',
                  originalValue: String(overrideValue ?? ''),
                });
              }
              return;
            }
            const allowed = fieldConfig.enumValues || [];
            if (allowed.length > 0) {
              const normalized = enumRaw.toLowerCase();
              const matched = allowed.find((v) => v.toLowerCase() === normalized);
              if (!matched) {
                errors.push({
                  rowIndex,
                  fieldKey,
                  type: 'TYPE',
                  message: `Ungültiger Wert. Erlaubt: ${allowed.join(', ')}`,
                  originalValue: String(overrideValue),
                });
                return;
              }
              value = fieldConfig.enumNormalize ? fieldConfig.enumNormalize(matched) : matched;
            } else {
              value = enumRaw;
            }
            break;
          }
          case 'number': {
            const n = typeof overrideValue === 'number' ? overrideValue : Number(String(overrideValue).replace(',', '.'));
            if (Number.isNaN(n)) {
              errors.push({ rowIndex, fieldKey, type: 'TYPE', message: 'Zahl erwartet', originalValue: String(overrideValue) });
              return;
            }
            value = n;
            break;
          }
          case 'boolean': {
            if (typeof overrideValue === 'boolean') {
              value = overrideValue;
            } else {
              const lower = String(overrideValue).toLowerCase();
              if (['true', 'wahr', 'ja', '1'].includes(lower)) value = true;
              else if (['false', 'falsch', 'nein', '0'].includes(lower)) value = false;
              else {
                errors.push({ rowIndex, fieldKey, type: 'TYPE', message: 'Boolescher Wert erwartet', originalValue: String(overrideValue) });
                return;
              }
            }
            break;
          }
          case 'date': {
            if (!overrideValue) return;
            const d = new Date(overrideValue);
            if (Number.isNaN(d.getTime())) {
              errors.push({ rowIndex, fieldKey, type: 'DATE', message: 'Ungültiges Datum', originalValue: String(overrideValue) });
              return;
            }
            value = toBackendDateIso(d);
            break;
          }
          default: {
            const str = String(overrideValue).trim();
            if (!str && fieldConfig.required) {
              errors.push({ rowIndex, fieldKey, type: 'TYPE', message: 'Pflichtfeld darf nicht leer sein', originalValue: String(overrideValue) });
              return;
            }
            value = str || undefined;
          }
        }

        if (value !== undefined) {
          mappedValues[fieldKey] = value;
        }
      } catch (e: any) {
        errors.push({
          rowIndex,
          fieldKey,
          type: 'UNKNOWN',
          message: e?.message || 'Unbekannter Fehler',
          originalValue: String(overrideValue),
        });
      }
    });

    // Neue Logik: Fallback-Werte auch dann setzen, wenn das Feld keiner CSV-Spalte zugeordnet ist
    config.fields.forEach((field) => {
      const key = field.key as string;
      const hasValue = Object.prototype.hasOwnProperty.call(mappedValues, key);
      if (!hasValue) {
        if (field.fallbackValue !== undefined) {
          mappedValues[key] = field.fallbackValue;
          return;
        }

        // Required-Felder ohne jeglichen Wert als Fehler markieren.
        // WICHTIG: requiredRelation-Felder (z.B. Mannschaft) werden bereits separat geprüft (hasAllRequiredRelations
        // und Relation-Overrides) und sollen hier keinen zusätzlichen Fehler verursachen.
        const isRequiredRelation = field.type === 'relation' && (field as any).requiredRelation;
        if (field.required && !isRequiredRelation) {
          errors.push({
            rowIndex,
            fieldKey: key,
            type: 'TYPE',
            message: 'Pflichtfeld darf nicht leer sein',
            originalValue: '',
          });
        }
      }
    });

    parsed.push({
      rawValues: rawRow,
      originalRowIndex: rowIndex,
      mappedValues,
      errors,
    });
  });

  return parsed;
}
